import { Show } from '../types/Show';

const LIVE_SHOW_PREVIEW_STORAGE_KEY = 'eruption-radio-live-show-preview-v1';
export const LIVE_SHOW_PREVIEW_MAX_AGE_MS = 10 * 60 * 1000;

interface LiveShowPreviewSnapshot {
  version: 1;
  cachedAt: number;
  show: Show;
}

const isValidShowPreview = (value: unknown): value is LiveShowPreviewSnapshot => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const snapshot = value as Partial<LiveShowPreviewSnapshot>;
  const show = snapshot.show as Partial<Show> | undefined;

  return (
    snapshot.version === 1 &&
    typeof snapshot.cachedAt === 'number' &&
    Boolean(show) &&
    typeof show?.show === 'string' &&
    typeof show?.show_id === 'string' &&
    typeof show?.date_start_time === 'string' &&
    Boolean(show?.presenter) &&
    typeof show?.presenter?.name === 'string' &&
    typeof show?.presenter?.genres === 'string' &&
    Boolean(show?.images) &&
    typeof show?.images === 'object'
  );
};

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const readLiveShowPreviewSnapshot = (
  maxAgeMs: number = LIVE_SHOW_PREVIEW_MAX_AGE_MS
): LiveShowPreviewSnapshot | null => {
  const storage = getStorage();
  if (!storage) {
    return null;
  }

  try {
    const rawValue = storage.getItem(LIVE_SHOW_PREVIEW_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    if (!isValidShowPreview(parsed)) {
      storage.removeItem(LIVE_SHOW_PREVIEW_STORAGE_KEY);
      return null;
    }

    if (Date.now() - parsed.cachedAt > maxAgeMs) {
      storage.removeItem(LIVE_SHOW_PREVIEW_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch {
    storage.removeItem(LIVE_SHOW_PREVIEW_STORAGE_KEY);
    return null;
  }
};

export const readLiveShowPreview = (
  maxAgeMs: number = LIVE_SHOW_PREVIEW_MAX_AGE_MS
): Show | null => {
  return readLiveShowPreviewSnapshot(maxAgeMs)?.show ?? null;
};

export const persistLiveShowPreview = (show: Show): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  const snapshot: LiveShowPreviewSnapshot = {
    version: 1,
    cachedAt: Date.now(),
    show,
  };

  try {
    storage.setItem(LIVE_SHOW_PREVIEW_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore storage failures so live show rendering still works from network data.
  }
};

export const clearLiveShowPreview = (): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(LIVE_SHOW_PREVIEW_STORAGE_KEY);
  } catch {
    // Ignore storage failures so live show rendering still works from network data.
  }
};
